import template from 'lodash/template';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { fetchCampaignOverview } from './campaignOverview';
import { subcampaignRemovedFromParticipantList } from './campaignParticipants';
import { individualSubcampaignRevoked } from 'modules/subcampaigns/reducers/subcampaign';

import {
  trackEventCampaignInfluencerInvited,
  trackEventCampaignInfluencerRemoved
} from '../../tracking/utils/tracking';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { SUBCAMPAIGN_REVOKED_BY_BRAND, SUBCAMPAIGN_STATE_REVOKED } from 'utils/subcampaign';
import { Querystring } from 'utils/url';
import { DISABLED_OUTFIT_STATUSES } from 'utils/realm-outfits';
import { convertListToHumanReadable } from 'utils/helper/common';
import { API_CODE_TASK_CONTAINS_BINDING_OUTFITS } from 'utils/status';

applyTranslations(require('utils/i18n/subcampaign.i18n.json'));
applyTranslations(require('modules/feedback/components/subcampaign-cancellation-feedback-modal.i18n.json'));

const SUBCAMPAIGN_REMOVED = 'campaignSubcampaigns/SUBCAMPAIGN_REMOVED';
const SUBCAMPAIGN_REVOKED = 'campaignSubcampaigns/SUBCAMPAIGN_REVOKED';
const CLEAN_LIST = 'campaignSubcampaigns/CLEAN_LIST';
const RECEIVE = 'campaignSubcampaigns/RECEIVE';
const RESET = 'campaignSubcampaigns/RESET';
const LOADING_STATE_CHANGED = 'campaignSubcampaigns/LOADING_STATE_CHANGED';

const { CAMPAIGNS, SUBCAMPAIGNS } = API.END_POINTS;

const SUBCAMPAIGN_MSG_KEY = 'subcampaign';
const SUBCAMPAIGN_CANCELED_MSG_KEY = 'subcampaign_cancellation_feedback_modal';

const initialState = {
  list: [],
  campaignId: null,
  isLoading: false
};

function subcampaignRemoved(data) {
  return {
    type: SUBCAMPAIGN_REMOVED,
    payload: {
      data
    }
  };
}

function subcampaignRevoked(data) {
  return {
    type: SUBCAMPAIGN_REVOKED,
    payload: {
      data
    }
  };
}

function receiveSubcampaigns(data, campaignId) {
  return {
    type: RECEIVE,
    payload: {
      data,
      campaignId
    }
  };
}

export function loadingStateChanged(data) {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      data
    }
  };
}

export function cleanSubcampaigns(campaignId) {
  return {
    type: CLEAN_LIST,
    payload: {
      campaignId
    }
  };
}

export function resetSubcampaigns() {
  return {
    type: RESET,
    payload: {}
  };
}

export function fetchSubcampaigns(
  campaignId,
  {
    showLoadingComponent = true,
    includeDeliverablesOverview = false,
    acceptedOnly = false
  }
) {
  let url = CAMPAIGNS.getCampaignSubcampaigns(campaignId);

  const includes = [];

  if (includeDeliverablesOverview) {
    includes.push('deliverables_overview');
  }

  const queryParams = {};

  if (includes.length > 0) {
    queryParams.include = includes.join(',');
  }

  if (acceptedOnly) {
    queryParams.states = 'accepted';
  }

  const queryString = Querystring.objectToString(queryParams);

  if (queryString) {
    url += `?${queryString}`;
  }

  return (dispatch) => {
    if (showLoadingComponent) {
      dispatch(showLoading(url));
    }

    dispatch(loadingStateChanged(true));
    dispatch(cleanSubcampaigns(campaignId));

    return get(url)
      .then((response) => {
        dispatch(loadingStateChanged(false));
        dispatch(receiveSubcampaigns(response.data || [], campaignId));

        if (showLoadingComponent) {
          dispatch(hideLoading(url));
        }
      })
      .catch((error) => {
        dispatch(loadingStateChanged(false));

        if (showLoadingComponent) {
          dispatch(hideLoading(url));
        }

        throw error;
      });
  };
}

export function createSubcampaign(influencerId, campaignId) {
  const url = CAMPAIGNS.getCampaignSubcampaigns(campaignId);

  return (dispatch) => {
    dispatch(showLoading(url));

    const data = {
      creatorId: influencerId
    };

    return post(url, { data })
      .then((response) => {
        const subcampaign = response.data;

        trackEventCampaignInfluencerInvited(subcampaign);
        dispatch(hideLoading(url));

        return subcampaign;
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        dispatch(danger(__t(`${SUBCAMPAIGN_MSG_KEY}.errors.system`)));
        throw error;
      });
  };
}

export function removeSubcampaign(subcampaign, campaignId) {
  const url = CAMPAIGNS.removeSubcampaign(subcampaign.id);

  return (dispatch) => {
    dispatch(showLoading(url));

    return destroy(url, {}, { suppressGlobalNotification: true })
      .then(() => {
        dispatch(subcampaignRemoved(subcampaign));
        dispatch(fetchCampaignOverview(campaignId));
        dispatch(subcampaignRemovedFromParticipantList(subcampaign));
        trackEventCampaignInfluencerRemoved(subcampaign);
        dispatch(hideLoading(url));
      })
      .catch((err) => {
        dispatch(hideLoading(url));
        logError(err);
        dispatch(danger(__t(`${SUBCAMPAIGN_MSG_KEY}.errors.system`)));
      });
  };
}

export function cancelSubcampaign(subcampaign, disagreementDetails, campaignType = 'collab') {
  const url = SUBCAMPAIGNS.cancelSubcampaign(subcampaign.id);

  return (dispatch) => {
    dispatch(showLoading(url));

    const data = {
      reason: disagreementDetails.reason,
      comment: disagreementDetails.comment
    };

    return post(url, { data })
      .then(() => {
        dispatch(subcampaignRevoked(subcampaign)); // update list
        dispatch(individualSubcampaignRevoked(subcampaign.id)); // update individual offer
        dispatch(fetchCampaignOverview(subcampaign.campaign.id, campaignType));
        dispatch(subcampaignRemovedFromParticipantList(subcampaign));
        trackEventCampaignInfluencerRemoved(subcampaign);
        dispatch(hideLoading(url));
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.code;
        const errorMsgByCode = __t(`${SUBCAMPAIGN_CANCELED_MSG_KEY}.errors.${errorCode}`);
        let errorMsg = errorMsgByCode || __t(`${SUBCAMPAIGN_CANCELED_MSG_KEY}.errors.generic.canceled`);

        if (errorCode === API_CODE_TASK_CONTAINS_BINDING_OUTFITS) {
          errorMsg = template(errorMsg)({
            outfitStatuses: convertListToHumanReadable(DISABLED_OUTFIT_STATUSES)
          });
        }

        logError(error);

        dispatch(danger(errorMsg));
        dispatch(hideLoading(url));
      });
  };
}

export function uncancelSubcampaign(subcampaign, campaignType = 'collab') {
  const url = SUBCAMPAIGNS.uncancelSubcampaign(subcampaign.id);

  return (dispatch) => {
    dispatch(showLoading(url));

    return destroy(url)
      .then(() => {
        dispatch(fetchCampaignOverview(subcampaign.campaign.id, campaignType));
        trackEventCampaignInfluencerInvited(subcampaign);
        dispatch(hideLoading(url));
      })
      .catch(() => dispatch(hideLoading(url)));
  };
}

export default createReducer('campaignSubcampaigns', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [SUBCAMPAIGN_REMOVED]: (state, { data }) => ({
    ...state,
    list: state.list.filter(subcampaign => subcampaign.id !== data.id)
  }),
  [SUBCAMPAIGN_REVOKED]: (state, { data }) => ({
    ...state,
    list: state.list.map(subcampaign => (
      subcampaign.id !== data.id
        ? subcampaign
        : {
          ...subcampaign,
          state: SUBCAMPAIGN_STATE_REVOKED,
          revokedBy: SUBCAMPAIGN_REVOKED_BY_BRAND
        }
    ))
  }),
  [RECEIVE]: (state, { data, campaignId }) => ({
    ...state,
    list: data,
    campaignId
  }),
  [CLEAN_LIST]: (state, { campaignId }) => ({
    ...state,
    list: campaignId === state.campaignId ? state.list : [],
    campaignId
  }),
  [LOADING_STATE_CHANGED]: (state, { data }) => ({
    ...state,
    isLoading: data
  })
});
